import { stateGetters, stateSetters, watchGetterAndPersist } from '@/utils/store/store-utils'
import { preserveCollectionPinPositions } from '@/utils/store/preserve-pin-position'

import Collection, { SHOW_COUNT } from '@/store/models/collections/Collection'
import CollectionMember from '@/store/models/collections/CollectionMember'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import ResourceTag from '@/store/models/collectionItems/ResourceTag'
import User from '@/store/models/user/User'
import UserComment from '@/store/models/user/UserComment'
import UserLike from '@/store/models/user/UserLike'
import { COLLECTION_SORT_FIELDS, collectionsFilterDefaults, ITEM_LAYOUT_STYLE } from '@/utils/constants'
import Preferences from '@/plugins/preferences'
import bus from '@/plugins/eventbus'
import inviteActions from './inviteActions'
import membershipActions from './membershipActions'

const defaultState = () => ({
  cursor: null,
  show: SHOW_COUNT,
  totalCount: 0,
  pinnedTotalCount: 0,
  // Same counts ignoring the search term, so the header can render
  // "3 out of 40 collections". Equal to the above when no search is active.
  unfilteredTotalCount: 0,
  pinnedUnfilteredTotalCount: 0,
  listStyle: ITEM_LAYOUT_STYLE.cards,
  publicOwner: null,
  unreachableId: null,
  filters: {
    search: '',
    sorting: [COLLECTION_SORT_FIELDS.createdAt, false],
  },
})

export default {
  name: 'collections',
  namespaced: true,
  state: defaultState(),
  getters: {
    ...stateGetters(defaultState()),
    defaultFilterFn() {
      return collectionsFilterDefaults
    },
    filters(s, g) {
      const { filters } = s
      return filters ?? g.defaultFilterFn()
    },
    sorting(_, g) {
      return g.filters.sorting
    },
  },
  mutations: {
    ...stateSetters(defaultState()),
    reset(state) {
      const s = defaultState()
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    },
    setFilters(state, { filters }) {
      state.filters = {
        ...state.filters,
        ...filters,
      }
    },
  },
  actions: {
    setSortingAction({
      commit, getters,
    }, val) {
      const { filters } = getters
      commit('setFilters', { filters: { ...filters, sorting: val } })
    },
    async getPinnedCollections({ commit }, params = {}) {
      const { results, next, total, unfilteredTotal } = await this.$api.collection.getPinned({
        params: { limit: 25, ...params },
      })
      await Collection.insertOrUpdate({ data: results })

      if (typeof total === 'number') commit('setPinnedTotalCount', total)

      // An API build predating `unfilteredTotal` would otherwise leave the
      // counter at 0 and render "0 collections" during a deploy skew. Without
      // a search the two totals are equal anyway, so `total` is a safe fallback.
      const pinnedUnfiltered = typeof unfilteredTotal === 'number' ? unfilteredTotal : total
      if (typeof pinnedUnfiltered === 'number') commit('setPinnedUnfilteredTotalCount', pinnedUnfiltered)

      return { results, next, total, unfilteredTotal }
    },

    async getCollections({ getters, commit }, { signal, ...params } = {}) {
      const { results, next, total, unfilteredTotal } = await this.$api.collection.get({
        params: { ...(getters.cursor && { next: getters.cursor }), limit: 10, ...params },
        ...(signal && { signal }),
      })

      const offset = getters.show - SHOW_COUNT
      results.forEach((item, index) => {
        item._sortPosition = offset + index
      })

      preserveCollectionPinPositions(results)
      await Collection.insertOrUpdate({ data: results })

      if (typeof total === 'number') commit('setTotalCount', total)

      // See getPinnedCollections: fall back to `total` on older API builds.
      const unfiltered = typeof unfilteredTotal === 'number' ? unfilteredTotal : total
      if (typeof unfiltered === 'number') commit('setUnfilteredTotalCount', unfiltered)

      return { results, next, total, unfilteredTotal }
    },
    async searchCollections(_, params) {
      const { results, next } = await this.$api.collection.get({ params })
      return { results, next }
    },
    async getById({ commit, getters }, id) {
      if (getters.unreachableId === id) commit('setUnreachableId', null)
      try {
        const collection = await this.$api.collection.getId(id)
        preserveCollectionPinPositions(collection)
        await Collection.insertOrUpdate({ data: collection })

        return collection
      } catch (error) {
        if (error?.message === 'NOT_FOUND' || error?.message === 'INSUFFICIENT_PRIVILEGE') {
          commit('setUnreachableId', id)
          return null
        }
        throw error
      }
    },
    async getSubCollections(_, parentCollectionId) {
      const { results } = await this.$api.collection.get({
        params: { parentCollectionId, showSubCollections: true },
      })

      await Collection.insertOrUpdate({ data: results })

      return results
    },
    async pinCollection({ commit }, { collectionId, position }) {
      const result = await this.$api.collection.pinCollection(collectionId, { position })
      await Collection.insertOrUpdate({ data: result })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    async unpinCollection({ commit }, { collectionId }) {
      await this.$api.collection.unpinCollection(collectionId)
      await Collection.update({
        where: collectionId,
        data: { pinPosition: null },
      })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return true
    },

    async pinSubCollection({ commit }, { collectionId, subCollectionId, position }) {
      const result = await this.$api.collection.pinSubCollection(collectionId, subCollectionId, { position })
      await Collection.insertOrUpdate({ data: result })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    async unpinSubCollection({ commit }, { collectionId, subCollectionId }) {
      await this.$api.collection.unpinSubCollection(collectionId, subCollectionId)
      await Collection.update({
        where: subCollectionId,
        data: { pinPosition: null },
      })
      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return true
    },

    async deleteCollection(_, collection) {
      await this.$api.collection.remove(collection.id)

      await Collection.delete(collection.id)
      await CollectionMember.delete(i => i.collectionId === collection.id)
      await CollectionItem.delete(i => i.ownerId === collection.id)

      return true
    },
    async editCollection(_, { id, data }) {
      const result = await this.$api.collection.edit(id, data)
      await Collection.update({ where: id, data: result })
      return result
    },
    async uploadImage(_, { id, file }) {
      const { data: image } = await this.$api.collection.uploadImage(id, file)
      await Collection.update({ where: id, data: { image } })
      return image
    },
    async removeImage(_, { id }) {
      await this.$api.collection.removeImage(id)
      await Collection.update({ where: id, data: { image: null } })
    },
    async createCollection(_, data) {
      return this.$api.collection.create({
        name: data.name,
        description: data.description,
        defaultSorting: data.defaultSorting,
        defaultView: data.defaultView,
        subCollectionPosition: data.subCollectionPosition || 'first',
        parent: data.parent || null,
        access: data.access || [],
        isPublic: data.isPublic || false,
      })
    },
    // Public collection actions
    async getPublicPinnedCollections({ commit }, { username, params = {} }) {
      const { owner, results, next } = await this.$api.collection.getPublicPinnedCollections(username, {
        params,
      })
      await Collection.insertOrUpdate({ data: results })
      if (owner) commit('setPublicOwner', owner)
      return { owner, results, next }
    },

    async getPublicCollectionPinnedItems(_, { username, collectionId, params = {} }) {
      const data = await this.$api.collection.getPublicCollectionPinnedItems(username, collectionId, {
        params,
      })

      if (data.results?.length) {
        const itemsToInsert = data.results.map((item, idx) => {
          const { owner, tags, addedBy, ...rest } = item
          return {
            ...rest,
            id: item._id,
            ownerId: collectionId,
            addedById: addedBy || null,
            tagIds: tags?.map(t => t._id) || [],
            tags: tags?.map(t => ({ ...t, id: t._id, ownerId: t.owner })) || [],
            sortOrder: idx,
          }
        })
        await CollectionItem.insertOrUpdate({ data: itemsToInsert })
      }

      return data
    },

    async getPublicMixedPinned(_, { username, collectionId }) {
      const { results } = await this.$api.collection.getPublicMixedPinned(username, collectionId)

      const pinnedItems = []
      const pinnedCollections = []

      for (const result of results) {
        if (result._pinResourceType === 'CollectionItem') {
          const { owner, tags, addedBy, ...rest } = result
          pinnedItems.push({
            ...rest,
            id: result._id,
            ownerId: collectionId,
            addedById: addedBy || null,
            tagIds: tags?.map(t => t._id) || [],
            tags: tags?.map(t => ({ ...t, id: t._id, ownerId: t.owner })) || [],
            sortOrder: 0,
          })
        } else {
          pinnedCollections.push({
            ...result,
            ownerId: result.owner?._id || result.owner,
            parent: collectionId,
            isPublic: true,
            members: (result.membersPreview || []).map(t => ({
              ...t,
              userId: t.user?._id,
              collectionId: result._id,
            })),
          })
        }
      }

      if (pinnedItems.length) {
        await CollectionItem.insertOrUpdate({ data: pinnedItems })
      }
      if (pinnedCollections.length) {
        await Collection.insertOrUpdate({ data: pinnedCollections })
      }

      return { pinnedItemCount: pinnedItems.length }
    },

    async getPublicCollections({ commit }, { username, params = {} }) {
      const { owner, results, next } = await this.$api.collection.getPublicCollections(username, {
        params,
      })

      const offset = Collection.query().where('_sortPosition', v => v != null).count()
      results.forEach((item, index) => {
        item._sortPosition = offset + index
      })

      preserveCollectionPinPositions(results)
      await Collection.insertOrUpdate({ data: results })
      if (owner) commit('setPublicOwner', owner)
      return { owner, results, next }
    },
    async getPublicCollection({ commit }, { username, collectionId }) {
      const collection = await this.$api.collection.getPublicCollection(username, collectionId)
      await Collection.insertOrUpdate({ data: collection })
      if (collection.owner) commit('setPublicOwner', collection.owner)
      return collection
    },
    async getPublicCollectionByShortId(_, { shortId }) {
      return this.$api.collection.getPublicCollectionByShortId(shortId)
    },
    async getPublicCollectionItems(_, { username, collectionId, params = {} }) {
      const data = await this.$api.collection.getPublicCollectionItems(username, collectionId, {
        params,
      })

      // Insert items into Vuex ORM so they can be used with existing ItemList components
      // We must destructure relationship-named fields (owner, tags, addedBy) out of the
      // spread to prevent conflicts with Vuex ORM's belongsTo/hasManyBy normalization,
      // and instead set the explicit foreign key fields.
      if (data.results?.length) {
        const existingCount = CollectionItem.query().where('ownerId', collectionId).count()
        const itemsToInsert = data.results.map((item, idx) => {
          const { owner, tags, addedBy, ...rest } = item
          return {
            ...rest,
            id: item._id,
            ownerId: collectionId,
            addedById: addedBy || null,
            tagIds: tags?.map(t => t._id) || [],
            tags: tags?.map(t => ({ ...t, id: t._id, ownerId: t.owner })) || [],
            sortOrder: existingCount + idx,
          }
        })
        await CollectionItem.insertOrUpdate({ data: itemsToInsert })
      }

      return data
    },
    async getPublicSubCollections(_, { username, collectionId, params = {} }) {
      const { results, next } = await this.$api.collection.getPublicSubCollections(username, collectionId, {
        params,
      })
      preserveCollectionPinPositions(results)
      await Collection.insertOrUpdate({ data: results })
      return { results, next }
    },
    async getPublicCollectionStats(_, { username, collectionId }) {
      return this.$api.collection.getPublicCollectionStats(username, collectionId)
    },
    async getPublicCollectionPopularTags(_, { username, collectionId }) {
      const tags = await this.$api.collection.getPublicCollectionPopularTags(username, collectionId)
      if (tags?.length) {
        await ResourceTag.insertOrUpdate({ data: tags.map(t => ({ ...t, ownerId: t.owner })) })
      }
      return tags
    },

    // --- Public collection item actions ---

    async getPublicCollectionItem(_, { username, collectionId, itemId }) {
      const item = await this.$api.collection.getPublicCollectionItem(username, collectionId, itemId)

      // The endpoint now returns serialized data with likes (count) and
      // isLikedByUser (boolean). Normalize tags and foreign keys for Vuex ORM.
      const { owner, tags, addedBy, ...rest } = item

      const normalizedItem = {
        ...rest,
        id: item._id,
        ownerId: collectionId,
        addedById: addedBy || null,
        tagIds: tags?.map(t => t._id) || [],
        tags: tags?.map(t => ({ ...t, id: t._id, ownerId: t.owner })) || [],
      }

      await CollectionItem.insertOrUpdate({ data: normalizedItem })
      return normalizedItem
    },

    // Public comments
    async getPublicCollectionItemComments(_, { username, collectionId, itemId, params = {} }) {
      const data = await this.$api.collection.getPublicCollectionItemComments(username, collectionId, itemId, { params })
      if (data.results?.length) {
        // Insert user records so avatars/usernames resolve via Vuex ORM
        const users = data.results.map(i => i.user).filter(Boolean)
        if (users.length) await User.insertOrUpdate({ data: users })
        await UserComment.insertOrUpdate({ data: data.results })
      }
      return data
    },
    async postPublicCollectionItemComment(_, { username, collectionId, itemId, text }) {
      const comment = await this.$api.collection.postPublicCollectionItemComment(username, collectionId, itemId, { text })
      await UserComment.insertOrUpdate({ data: comment })
      // Update comment count on the item
      const item = CollectionItem.find(itemId)
      if (item) {
        await CollectionItem.update({ where: itemId, data: { commentsCount: (item.commentsCount || 0) + 1 } })
      }
      return comment
    },
    async deletePublicCollectionItemComment(_, { username, collectionId, itemId, commentId }) {
      await this.$api.collection.deletePublicCollectionItemComment(username, collectionId, itemId, commentId)
      await UserComment.delete(commentId)
      // Update comment count on the item
      const item = CollectionItem.find(itemId)
      if (item) {
        await CollectionItem.update({ where: itemId, data: { commentsCount: Math.max(0, (item.commentsCount || 0) - 1) } })
      }
      return true
    },

    // Public likes
    async getPublicCollectionItemLikes(_, { username, collectionId, itemId, params = {} }) {
      const data = await this.$api.collection.getPublicCollectionItemLikes(username, collectionId, itemId, { params })
      if (data.results?.length) {
        // Insert user records so avatars/usernames resolve via Vuex ORM
        const users = data.results.map(i => i.user).filter(Boolean)
        if (users.length) await User.insertOrUpdate({ data: users })
        await UserLike.insertOrUpdate({ data: data.results })
      }
      return data
    },
    async postPublicCollectionItemLike(_, { username, collectionId, itemId }) {
      const like = await this.$api.collection.postPublicCollectionItemLike(username, collectionId, itemId)
      await UserLike.insertOrUpdate({ data: like })
      // Update like count on the item
      const item = CollectionItem.find(itemId)
      if (item) {
        await CollectionItem.update({ where: itemId, data: { likes: (item.likes || 0) + 1, isLikedByUser: true } })
      }
      return like
    },
    async deletePublicCollectionItemLike(_, { username, collectionId, itemId }) {
      await this.$api.collection.deletePublicCollectionItemLike(username, collectionId, itemId)
      // Update like count on the item
      const item = CollectionItem.find(itemId)
      if (item) {
        await CollectionItem.update({ where: itemId, data: { likes: Math.max(0, (item.likes || 0) - 1), isLikedByUser: false } })
      }
      return true
    },

    // Public copy
    async copyPublicCollectionItem(_, { username, collectionId, itemId, ...data }) {
      return this.$api.collection.copyPublicCollectionItem(username, collectionId, itemId, data)
    },

    async copyPublicCollectionItemsBulk(_, { username, collectionId, ...data }) {
      return this.$api.collection.copyPublicCollectionItemsBulk(username, collectionId, data)
    },

    ...inviteActions,
    ...membershipActions,
  },
}

bus.$once('app:created', async app => {
  const listStyle = await Preferences.get('collectionStyle')
  if (ITEM_LAYOUT_STYLE[listStyle]) {
    app.config.globalProperties.$store.commit('collections/setListStyle', ITEM_LAYOUT_STYLE[listStyle])
  }

  watchGetterAndPersist(app.config.globalProperties.$store, 'collections/listStyle', 'collectionStyle')
})
